import { Player } from '@lottiefiles/react-lottie-player'
import { motion, useAnimate, type AnimationSequence } from 'framer-motion'
import { useCallback, useEffect, useState } from 'react'
import { useListenerMessage } from '../../../../hooks/subscription/use-listener-message'
import { useSub } from '../../../../hooks/subscription/use-sub'
import type { GameBoxRoom } from '../../../../services/games/games-boxes-rooms/types'
import { useGlobalStore } from '../../../../store/global-store'
import gift from '../assets/gift-animated.json'
import { Timer } from './timer'

const DURATION = 1
const COIN_DROP = '/COIN-DROP'

interface Props {
  coinDrop: GameBoxRoom
  elementPosition: null | {
    x: number
    y: number
  }
  handleRemoveCoinDrop: (id: number) => void
}

function CoinDrop({ coinDrop, elementPosition, handleRemoveCoinDrop }: Props) {
  const [scope, animate] = useAnimate()

  useEffect(() => {
    if (elementPosition) {
      const sequence: AnimationSequence = [
        [scope.current, { opacity: 1 }, { duration: 0.2 }],
        [scope.current, elementPosition, { duration: DURATION, delay: 2 }],
        [scope.current, { opacity: 0 }, { duration: 0.5 }],
      ]

      animate(sequence).then(() => handleRemoveCoinDrop(coinDrop?.id))
    }
  }, [scope, elementPosition, coinDrop?.id, animate, handleRemoveCoinDrop])

  return (
    <motion.div
      ref={scope}
      style={{ y: window.innerHeight / 2.7, x: window.innerWidth / 1.2 }}
      className="absolute h-10 w-10 opacity-0"
    >
      <div className="flex flex-col items-center justify-center w-full h-full">
        <Player loop autoplay src={gift} className="h-full w-full " />
        <img
          src={coinDrop?.user?.imgUser}
          className="w-1/2 h-1/2 absolute rounded-full"
        />
        {coinDrop?.dateFinalDistribute && (
          <Timer timeToEnd={coinDrop.dateFinalDistribute} />
        )}
      </div>
    </motion.div>
  )
}

export function GameBoxAnimation() {
  const [coinDrops, setCoinDrops] = useState<GameBoxRoom[]>([])

  const [elementPosition, setElementPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const liveId = useGlobalStore((state) => state.liveId)

  useListenerMessage((data) => {
    setElementPosition(JSON.parse(data))
  })

  useSub<GameBoxRoom>({
    endpoint: `${COIN_DROP}/${liveId}`,
    skip: !liveId,
    onData: (coinDrop) => {
      setCoinDrops((prevState) => prevState.concat(coinDrop))
    },
  })

  const handleRemoveCoinDrop = useCallback(
    (id: number) => {
      setCoinDrops((prevState) =>
        prevState.filter((coinDrop) => {
          return coinDrop.id !== id
        }),
      )
    },
    [setCoinDrops],
  )

  return (
    <div className="w-screen h-screen">
      {coinDrops.map((coinDrop) => (
        <CoinDrop
          key={coinDrop.id}
          coinDrop={coinDrop}
          elementPosition={elementPosition}
          handleRemoveCoinDrop={handleRemoveCoinDrop}
        />
      ))}
    </div>
  )
}
